
import axios from "axios";
import { tokenStorage } from "../utils/tokenStorage";

const API_BASE = process.env.REACT_APP_API_BASE || "https://api.gatiii.com/api";

console.log("🔧 API_BASE is:", API_BASE);
export const api = axios.create({
  baseURL: API_BASE,
});

// Public paths that should NOT have a token attached
const publicPaths = [
  "/users/login",
  "/users/signup",
  "/users/verify_email",
  "/users/forgot_password",
  "/users/reset_password",
  "/auth/refresh",                       // refresh has its own axios call, but safety net
  "/payment/webhook",
];

// Request interceptor — attach Bearer token to non-public requests
api.interceptors.request.use(
  (config) => {
    const isPublic = publicPaths.some((path) => config.url?.includes(path));
    if (!isPublic) {
      const token = tokenStorage.getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// =====================================================
// Refresh-token coordination
// =====================================================

const PUBLIC_PAGES_FOR_REDIRECT = [
  "/", "/signup", "/forgot-password", "/reset-password-with-otp",
  "/payment", "/health-log", "/about", "/terms-condition",
  "/privacy-policy", "/data-deletion",
];

const isOnPublicPage = () =>
  PUBLIC_PAGES_FOR_REDIRECT.includes(window.location.pathname);

// Module-level state to coordinate concurrent refresh attempts
let isRefreshing = false;
let pendingRequests = [];

const resumePendingRequests = (newAccessToken, error) => {
  pendingRequests.forEach((cb) => cb(newAccessToken, error));
  pendingRequests = [];
};

const refreshAccessToken = async () => {
  const refreshToken = tokenStorage.getRefreshToken();
  if (!refreshToken) throw new Error("No refresh token available");

  // Plain axios — NOT the api instance — so this call doesn't go through interceptors
  const response = await axios.post(
    `${API_BASE}/auth/refresh`,
    { refreshToken },
    { headers: { "Content-Type": "application/json" } }
  );

  const data = response.data;
  if (data?.status !== 200 || !data?.data) {
    throw new Error(data?.message || "Refresh failed");
  }

  const { accessToken, refreshToken: newRefreshToken } = data.data;
  tokenStorage.saveTokens(accessToken, newRefreshToken);
  return accessToken;
};

// Response interceptor — handle 401 (refresh + retry) and 403 (blocked)
// api.interceptors.response.use(
//   (response) => response,
//   async (error) => {
//     const originalRequest = error.config;
//     const status = error.response?.status;
//     const message = error.response?.data?.message || "";

//     // 401 → try refresh, then retry original request once
//     if (status === 401 && !originalRequest._retry ) {
//       // Don't try refresh for the refresh endpoint itself (avoid infinite loop)
//       if (originalRequest.url?.includes("/auth/refresh")) {
//         tokenStorage.clear();
//         window.location.href = "/";
//         return Promise.reject(error);
//       }

//       originalRequest._retry = true;

//       // Another request is already refreshing — queue this one
//       if (isRefreshing) {
//         return new Promise((resolve, reject) => {
//           pendingRequests.push((newToken, refreshError) => {
//             if (refreshError) {
//               reject(refreshError);
//             } else {
//               originalRequest.headers.Authorization = `Bearer ${newToken}`;
//               resolve(api(originalRequest));
//             }
//           });
//         });
//       }

//       // This request triggers the refresh
//       isRefreshing = true;
//       try {
//         const newAccessToken = await refreshAccessToken();
//         resumePendingRequests(newAccessToken, null);
//         originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
//         return api(originalRequest);
//       } catch (refreshError) {
//         resumePendingRequests(null, refreshError);
//         tokenStorage.clear();
//         window.location.href = "/";
//         return Promise.reject(refreshError);
//       } finally {
//         isRefreshing = false;
//       }
//     }

//     // 403 with "blocked" message — keep existing logic
//     if (
//       status === 403 &&
//       message.toLowerCase().includes("blocked") &&
//       !isOnPublicPage()
//     ) {
//       tokenStorage.clear();
//       alert(message || "Your account has been blocked. Please contact support.");
//       window.location.href = "/";
//     }

//     return Promise.reject(error);
//   }
// );

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;
    const message = error.response?.data?.message || "";

    // 401 → try refresh, then retry original request once
    if (status === 401 && !originalRequest._retry) {
      // Don't try refresh for the refresh endpoint itself
      if (originalRequest.url?.includes("/auth/refresh")) {
        tokenStorage.clear();
        if (!isOnPublicPage()) window.location.href = "/";
        return Promise.reject(error);
      }

      // No refresh token? Just give up.
      if (!tokenStorage.getRefreshToken()) {
        tokenStorage.clear();
        if (!isOnPublicPage()) window.location.href = "/";
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      // Concurrent refresh handling
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          pendingRequests.push((newToken, refreshError) => {
            if (refreshError) reject(refreshError);
            else {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              resolve(api(originalRequest));
            }
          });
        });
      }

      isRefreshing = true;
      try {
        const newAccessToken = await refreshAccessToken();
        resumePendingRequests(newAccessToken, null);
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        resumePendingRequests(null, refreshError);
        tokenStorage.clear();
        if (!isOnPublicPage()) window.location.href = "/";
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // 403 blocked-user — unchanged
    if (
      status === 403 &&
      message.toLowerCase().includes("blocked") &&
      !isOnPublicPage()
    ) {
      tokenStorage.clear();
      alert(message || "Your account has been blocked. Please contact support.");
      window.location.href = "/";
    }

    return Promise.reject(error);
  }
);

// =====================================================
// EXISTING APIs
// =====================================================

export const fetchBookingDetails = (bookingId) =>
  api.get(`/bookings/${bookingId}`);

export const getAllRides = () => api.get("/bookings");

export const createOrder = (data) => api.post("/payment/create-order", data);

export const forgotPassword = (userEmail) =>
  api.post("/users/forgot_password", { userEmail }, {
    headers: { "Content-Type": "application/json" },
  });

export const resetPassword = (userEmail, userPassword, userOTP) =>
  api.post("/users/reset_password", { userEmail, userPassword, userOTP }, {
    headers: { "Content-Type": "application/json" },
  });

export const updatePaymentStatus = (data) =>
  api.post("/payment/payments/status", data);

export const uploadImage = (file) => {
  const formData = new FormData();
  formData.append("image", file);
  return api.post("/drivers/upload-image", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

export const registerDriver = (driverData) =>
  api.post("/drivers/register", driverData);

export const getAllDrivers = () => api.get("/drivers/all");

export const bookRide = (rideData) => api.post("/bookings/create", rideData);

export const getAllUsers = () => api.get("/users");

export const updateUserRole = (email, newRole) =>
  api.put("/users/update_user_role", null, {
    params: { email, newRole },
  });

export const initiateRefund = (paymentId, amount) =>
  api.post("/payment/refund", null, {
    params: { paymentId, amount },
  });

export const getUserBookings = (userId) =>
  api.get(`/bookings/user/${userId}`);

export const confirmRide = (bookingId, isRideConfirmed, userId) =>
  api.put("/bookings/confirm", {
    bookingId,
    isRideConfirmed,
    userId,
  });

export const addDriverInfo = (bookingId, driverMobileNumber, pickupTime) =>
  api.put(`/bookings/add_driver_info/${bookingId}`, null, {
    params: { driverMobileNumber, pickupTime },
  });

export const refundRide = (paymentId, amount) =>
  api.post("/payment/refund", null, {
    params: { paymentId, amount },
  });

export const updateUserBlockStatus = (email, isBlocked) =>
  api.put("/users/update_user_block_status", null, {
    params: { email, isBlocked },
  });

export const deleteUser = (userId) => api.delete(`/users/${userId}`);

// =====================================================
// VENDOR MANAGEMENT
// =====================================================

export const getVendors = (category = "RIDE") =>
  api.get(`/admin/vendors?category=${category}`);

export const getAllVendors = () => api.get("/admin/vendors/all");

export const getVendorsByStatus = (status) =>
  api.get(`/admin/vendors/by-status?status=${status}`);

export const getVendorById = (vendorId) =>
  api.get(`/admin/vendors/${vendorId}`);

export const createVendor = (vendor) => api.post("/admin/vendors", vendor);

export const updateVendor = (vendorId, vendor) =>
  api.put(`/admin/vendors/${vendorId}`, vendor);

export const verifyVendor = (vendorId, notes = null) =>
  api.post(`/admin/vendors/${vendorId}/verify`, { notes });

export const rejectVendor = (vendorId, reason) =>
  api.post(`/admin/vendors/${vendorId}/reject`, { reason });

export const deactivateVendor = (vendorId) =>
  api.delete(`/admin/vendors/${vendorId}`);

export const reactivateVendor = (vendorId) =>
  api.post(`/admin/vendors/${vendorId}/reactivate`);

// =====================================================
// ASSIGNMENT MANAGEMENT
// =====================================================

export const assignVendor = (bookingId, payload) =>
  api.post(`/admin/assignments/${bookingId}/assign`, payload);

export const cancelAssignment = (assignmentId) =>
  api.delete(`/admin/assignments/${assignmentId}`);

export const updateAssignmentStatus = (assignmentId, status) =>
  api.put(`/admin/assignments/${assignmentId}/status`, { status });

export const getAssignmentForBooking = (bookingId) =>
  api.get(`/admin/assignments/booking/${bookingId}`);

export const getActiveAssignmentsForVendor = (vendorId) =>
  api.get(`/admin/assignments/vendor/${vendorId}/active`);

export const getAssignmentsByStatus = (status) =>
  api.get(`/admin/assignments/by-status?status=${status}`);

// =====================================================
// HEALTH LOG
// =====================================================

export const getDailyHealthLog = (date) =>
  api.get(`/health/daily?date=${date}`);

export const getHealthLogRange = (from, to) =>
  api.get(`/health/range?from=${from}&to=${to}`);

export const logSugarEntry = (payload) =>
  api.post("/health/sugar", payload);

export const logWeightEntry = (payload) =>
  api.post("/health/weight", payload);

export const deleteSugarEntry = (id) =>
  api.delete(`/health/sugar/${id}`);

export const deleteWeightEntry = (id) =>
  api.delete(`/health/weight/${id}`);

export const getUserFeatures = (userId) =>
  api.get(`/v1/admin/features/user/${userId}`);

export const updateUserFeature = (userId, featureKey, enabled) =>
  api.put(`/v1/admin/features/user`, { userId, featureKey, enabled });

export const getKpis = (params = {}) =>
  api.get('/v1/admin/kpi', { params });

export const uploadVendorDocument = (vendorId, fileType, file) => {
  const formData = new FormData();
  formData.append("file", file);
  return api.post(`/admin/vendors/${vendorId}/upload-document?fileType=${fileType}`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};